import React, { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { linkAccountThunk, verifyLinkAccountThunk } from "../features/account/accountThunk";
import { useNavigate } from "react-router-dom";
import Button from "../components/ui/Button";

const LinkOtpQueue: React.FC = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const { selectedFip } = useAppSelector((s) => s.fip);
    const { linkRefNumber, selectedAccountsToLink, loading, error } = useAppSelector((s) => s.account);

    const [otp, setOtp] = React.useState("");
    const [verifyLoading, setVerifyLoading] = React.useState(false);
    const [isLinking, setIsLinking] = React.useState(false);
    const [hasInitiatedLink, setHasInitiatedLink] = React.useState(false);

    useEffect(() => {
        if (selectedAccountsToLink.length === 0) {
            navigate(-1);
            return;
        }

        if (!hasInitiatedLink && selectedFip && !linkRefNumber && !isLinking) {
            setIsLinking(true);
            setHasInitiatedLink(true);
            dispatch(linkAccountThunk({
                fipId: selectedFip.FIP_ID,
                accounts: selectedAccountsToLink
            })).finally(() => {
                setIsLinking(false);
            });
        }
    }, [selectedAccountsToLink, hasInitiatedLink, selectedFip, linkRefNumber, isLinking, dispatch, navigate]);

    const handleVerifyOtp = () => {
        if (!linkRefNumber || !selectedFip) {
            alert("Something went wrong! Missing link session details. Please try going back.");
            return;
        }
        setVerifyLoading(true);
        dispatch(verifyLinkAccountThunk({
            otp,
            fipId: selectedFip.FIP_ID,
            refNumber: linkRefNumber
        })).then((res) => {
            setVerifyLoading(false);
            if (verifyLinkAccountThunk.fulfilled.match(res)) {
                // Verification successful. Journey completes.
                navigate("/connectCams");
            } else {
                alert(`OTP Verification Failed. Please try again.`);
            }
        });
    };

    return (
        <div className="flex flex-col min-h-screen bg-fin-bg p-6">
            <div className="flex-1 flex flex-col p-6 items-center w-full max-w-sm mx-auto mt-10 bg-fin-surface rounded-2xl shadow-xl border border-fin-border">

                <div className="h-16 w-16 bg-fin-primary/10 rounded-full flex items-center justify-center mb-8 border border-fin-primary/20">
                    <svg className="w-8 h-8 text-fin-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                </div>

                <div className="text-center mb-10 w-full">
                    <h2 className="text-xl font-bold text-white leading-snug">
                        Verify Account Link
                    </h2>
                    <p className="text-sm text-fin-slate mt-2 leading-relaxed">
                        Enter the OTP sent to your registered mobile number to link {selectedAccountsToLink.length} account(s) for {selectedFip?.FIP_NAME}.
                    </p>
                    {isLinking && (
                        <p className="text-sm text-fin-primary mt-4 animate-pulse">
                            Initiating link request...
                        </p>
                    )}
                    {linkRefNumber && !isLinking && (
                        <p className="text-xs text-fin-primary mt-4 font-mono bg-fin-primary/10 inline-block px-3 py-1.5 rounded-lg border border-fin-primary/20">
                            Ref: {linkRefNumber}
                        </p>
                    )}
                    {error && (
                         <p className="text-xs text-red-500 mt-4 font-mono bg-red-500/10 inline-block px-3 py-1.5 rounded-lg border border-red-500/20">
                            {error}
                        </p>
                    )}
                </div>

                <div className="w-full space-y-4 mb-10">
                    <input
                        type="text"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value)}
                        placeholder="Enter 6-digit OTP"
                        className="w-full px-4 py-4 rounded-xl bg-fin-bg border border-fin-border text-white focus:ring-2 focus:ring-fin-primary focus:border-transparent outline-none transition-all text-center text-xl tracking-[0.5em] font-mono"
                        maxLength={6}
                        disabled={isLinking || !linkRefNumber}
                    />
                </div>

                <div className="w-full flex gap-3 mt-auto">
                    <Button
                        variant="ghost"
                        onClick={() => navigate(-1)}
                        className="flex-1 text-fin-slate hover:text-white"
                    >
                        Back
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleVerifyOtp}
                        isLoading={verifyLoading || isLinking}
                        disabled={!otp || otp.length < 4 || isLinking || !linkRefNumber}
                        className="flex-[2] py-4"
                    >
                        Submit OTP
                    </Button>
                </div>

            </div>
        </div>
    );
};

export default LinkOtpQueue;
